<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTimer } from '@/shared/composables/useTimer'
import type { CoreType } from '../lib/types'
import { isFieldEntered, SCORING_FIELD_PLACEHOLDER } from '../lib/scoringInput'

const type     = defineModel<CoreType>('type',      { required: true })
const reps     = defineModel<string>('reps',        { required: true })
const plankMin = defineModel<string>('plankMin',    { required: true })
const plankSec = defineModel<string>('plankSec',    { required: true })
const exempt   = defineModel<boolean>('exempt',     { required: true })

const props = defineProps<{ componentScore: number | null }>()

const isOpen  = ref(false)
const isPlank = computed(() => type.value === 'plank')
const isClrc  = computed(() => type.value === 'clrc')

const { startCountdown, startStopwatch } = useTimer()

const timerBtnText = computed(() => {
  if (isPlank.value) return 'Start Plank Timer'
  if (isClrc.value)  return 'Start 2:00 Timer'
  return 'Start 1:00 Timer'
})

function fireTimer() {
  if (isPlank.value)     startStopwatch('Forearm Plank', 'plank')
  else if (isClrc.value) startCountdown(120, 'Cross-Leg Reverse Crunch')
  else                   startCountdown(60, 'Sit-ups')
}

const hasInput = computed(() => {
  if (isPlank.value) return isFieldEntered(plankMin.value) || isFieldEntered(plankSec.value)
  return isFieldEntered(reps.value)
})

const pillText = computed(() => {
  if (exempt.value) return 'EXEMPT'
  if (!hasInput.value) return '–'
  if (props.componentScore !== null) return props.componentScore.toFixed(1) + ' pts'
  return '–'
})
</script>

<template>
  <div class="acc-card">
    <div class="acc-header" @click="isOpen = !isOpen">
      <div class="acc-header-left">
        <span class="acc-title">Core</span>
        <span class="acc-pts">max 15 pts</span>
      </div>
      <label class="acc-exempt" @click.stop>
        <input type="checkbox" v-model="exempt" style="accent-color:var(--accent);width:14px;height:14px;" />
        Exempt
      </label>
      <span class="acc-score-pill" :class="{ 'has-score': !exempt && hasInput && componentScore !== null }">
        {{ pillText }}
      </span>
      <span class="acc-tap-hint">{{ isOpen ? 'tap to collapse' : 'tap to expand' }}</span>
      <span class="acc-chevron" :class="{ open: isOpen }" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </span>
    </div>

    <div v-show="isOpen" class="acc-body">
      <div :style="exempt ? { opacity: '0.35', pointerEvents: 'none' } : {}">
        <div class="row">
          <div class="field">
            <label>Type</label>
            <div class="toggle-group">
              <button :class="['toggle-btn', { active: type === 'situp' }]" @click="type = 'situp'">Sit-ups (1 min)</button>
              <button :class="['toggle-btn', { active: type === 'clrc' }]"  @click="type = 'clrc'">Cross Leg RC (2 min)</button>
              <button :class="['toggle-btn', { active: type === 'plank' }]" @click="type = 'plank'">Forearm Plank</button>
            </div>
          </div>

          <div v-if="!isPlank" class="field sm">
            <label>Reps</label>
            <input type="text" inputmode="numeric" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="reps" aria-label="Core reps" />
          </div>

          <template v-if="isPlank">
            <div class="field sm" style="flex:1;">
              <label>Min</label>
              <input type="text" inputmode="numeric" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="plankMin" aria-label="Plank minutes" />
            </div>
            <div class="field sm" style="flex:1;">
              <label>Sec</label>
              <input type="text" inputmode="numeric" :placeholder="SCORING_FIELD_PLACEHOLDER" v-model="plankSec" aria-label="Plank seconds" />
            </div>
          </template>
        </div>

        <div v-if="!exempt" style="display:block;margin-top:10px;">
          <button class="calc-timer-btn" @click="fireTimer">{{ timerBtnText }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
